import _defineProperty from "/workdir/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/workdir/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/objectWithoutProperties.js";
const _excluded = ["sx", "onClick", "icon", "as"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { useRef, useEffect, useState, useContext } from 'react';
import { Reel } from '@hub/reel';
import { CoreContainerContext } from '@hub/core-container';
import { Imposter } from '@hub/imposter';
import { ArrowLeftSmallIcon, ArrowRightSmallIcon } from '@hub/icon';
import { IconButton } from '@hub/button';
import { useToken, forwardRef, DEFAULT_BREAKPOINT, // useBreakpointValue looks unavoidable here due to the renderReelControls logic
// eslint-disable-next-line no-restricted-syntax
useBreakpointValue } from '@hub/design-system-base';
import debounce from 'lodash/debounce';
import { useWithIsAboveFoldProvider } from '../is-above-fold-provider';
import trackClickEvents, { categories, labels } from '../analytics/trackEvents';
import { AnalyticsImpressionTracker } from '../../analytics-observer';
import { InlineDeckItem } from './inline-deck-item';
import { jsx as _jsx } from "react/jsx-runtime";
const halfHeight = 2;
const MAX_MOBILE_VISIBLE_ITEM = 1;
const MAX_DESKTOP_VISIBLE_ITEM = 3;
const MOBILE_PEEKWIDTH = 0.2;
const DESKTOP_PEEKWIDTH = 0;
const MAX_VISIBLE_ITEM = [MAX_MOBILE_VISIBLE_ITEM, MAX_MOBILE_VISIBLE_ITEM, MAX_DESKTOP_VISIBLE_ITEM, MAX_DESKTOP_VISIBLE_ITEM];
const PEEKWIDTH = [MOBILE_PEEKWIDTH, MOBILE_PEEKWIDTH, DESKTOP_PEEKWIDTH, DESKTOP_PEEKWIDTH];
const buttonSizeToken = 'size-8';
export const ReelButton = forwardRef((_ref, ref) => {
  let {
    sx,
    onClick,
    icon,
    as
  } = _ref,
      rest = _objectWithoutProperties(_ref, _excluded);

  return /*#__PURE__*/_jsx(IconButton, {
    ref: ref,
    "aria-label": rest['aria-label'],
    sx: _objectSpread({
      borderRadius: '50%',
      boxShadow: '0px 12px 24px 0px rgba(0, 0, 0, 0.15)',
      width: buttonSizeToken,
      height: buttonSizeToken,
      minWidth: buttonSizeToken
    }, sx),
    icon: icon,
    as: as,
    colorScheme: "white",
    variant: "solid",
    "aria-hidden": "true",
    onClick: onClick,
    "data-id": "inline-deck_inline-deck-reel_ReelButton_IconButton"
  });
});

const renderReelControls = _ref2 => {
  let {
    buttonSize,
    buttonOffsetY,
    buttonAlignmentX
  } = _ref2;
  return (// eslint-disable-next-line react/display-name
    _ref3 => {
      let {
        hasOverflowingFrames,
        children,
        scrollNext,
        scrollPrev,
        scrollPosition
      } = _ref3;
      return hasOverflowingFrames ? /*#__PURE__*/_jsx(Imposter, {
        impose: /*#__PURE__*/_jsx(ReelButton, {
          "aria-label": "Scroll to next",
          sx: {
            display: ['none', null, 'flex'],
            visibility: scrollPosition === 'end' ? 'hidden' : 'visible',
            zIndex: 1
          },
          icon: /*#__PURE__*/_jsx(ArrowRightSmallIcon, {
            "data-id": "inline-deck_inline-deck-reel_ReelButton_ArrowRightSmallIcon"
          }),
          onClick: () => {
            scrollNext();

            if (scrollPosition !== 'end') {
              trackClickEvents.clicked(categories.INLINE_DECK, labels.NEXT_CLICK);
            }
          },
          "data-id": "inline-deck_inline-deck-reel_ReelButton"
        }),
        position: "top-right",
        marginTop: "calc(".concat(buttonOffsetY, " - ").concat(buttonSize, " / 2)"),
        marginRight: buttonAlignmentX === 'center' ? "calc(".concat(buttonSize, " / -2)") : undefined,
        "data-id": "inline-deck_inline-deck-reel_ReelButton_Imposter",
        children: /*#__PURE__*/_jsx(Imposter, {
          impose: /*#__PURE__*/_jsx(ReelButton, {
            "aria-label": "Scroll to previous",
            sx: {
              display: ['none', null, 'flex'],
              visibility: scrollPosition === 'start' ? 'hidden' : 'visible',
              zIndex: 1
            },
            icon: /*#__PURE__*/_jsx(ArrowLeftSmallIcon, {
              "data-id": "inline-deck_inline-deck-reel_ReelButton_ArrowLeftSmallIcon"
            }),
            onClick: () => {
              scrollPrev();

              if (scrollPosition !== 'start') {
                trackClickEvents.clicked(categories.INLINE_DECK, labels.PREVIOUS_CLICK);
              }
            },
            "data-id": "inline-deck_inline-deck-reel_ReelButton"
          }),
          position: "top-left",
          marginTop: "calc(".concat(buttonOffsetY, " - ").concat(buttonSize, " / 2)"),
          marginLeft: buttonAlignmentX === 'center' ? "calc(".concat(buttonSize, " / -2)") : undefined,
          "data-id": "inline-deck_inline-deck-reel_ReelButton_Imposter_Imposter",
          children: children
        })
      }) : children;
    }
  );
};

export const InlineDeckReel = _ref4 => {
  let {
    items,
    clickEvent,
    viewEvent
  } = _ref4;
  const buttonAlignmentX = useBreakpointValue(['edge', 'edge', 'center', 'center'], DEFAULT_BREAKPOINT);
  const buttonSize = useToken('sizes', buttonSizeToken);
  const container = useContext(CoreContainerContext);
  const {
    0: buttonOffsetY,
    1: setButtonOffsetY
  } = useState('50%');
  const elementButtonRelativeTo = useRef(null);
  useEffect(() => {
    var _elementButtonRelativ, _elementButtonRelativ2;

    setButtonOffsetY("".concat(Math.floor(((_elementButtonRelativ = elementButtonRelativeTo === null || elementButtonRelativeTo === void 0 ? void 0 : (_elementButtonRelativ2 = elementButtonRelativeTo.current) === null || _elementButtonRelativ2 === void 0 ? void 0 : _elementButtonRelativ2.clientHeight) !== null && _elementButtonRelativ !== void 0 ? _elementButtonRelativ : 0) / halfHeight), "px"));

    if (!window.ResizeObserver) {
      return undefined;
    }

    const resizeHandler = debounce(entries => {
      setButtonOffsetY("".concat(Math.floor(entries[0].contentRect.height / halfHeight), "px"));
    }, 100, {
      leading: true,
      trailing: true
    });
    const resizeObserver = new ResizeObserver(resizeHandler);

    if (elementButtonRelativeTo !== null && elementButtonRelativeTo !== void 0 && elementButtonRelativeTo.current) {
      resizeObserver.observe(elementButtonRelativeTo === null || elementButtonRelativeTo === void 0 ? void 0 : elementButtonRelativeTo.current);
    }

    return () => {
      resizeHandler.cancel();
      resizeObserver.disconnect();
    };
  }, []);
  const analyticsTrackerWithFold = useWithIsAboveFoldProvider(AnalyticsImpressionTracker);
  return /*#__PURE__*/_jsx(Reel, {
    snap: true,
    gap: ['spacing-sm', null, 'spacing-md'],
    containerOverflow: [container.gutter, null, 'spacing-none'],
    visibleFrames: MAX_VISIBLE_ITEM,
    peekWidth: PEEKWIDTH,
    shouldWrapChildren: false,
    renderControls: renderReelControls({
      buttonSize,
      buttonOffsetY,
      buttonAlignmentX
    }),
    "data-id": "inline-deck_inline-deck-reel_ReelButton_Reel",
    children: items.map((item, index) => {
      const AnalyticsTrackerWithFold = analyticsTrackerWithFold(index < MAX_DESKTOP_VISIBLE_ITEM ? 'above' : 'below');
      return /*#__PURE__*/_jsx(AnalyticsTrackerWithFold, _objectSpread(_objectSpread({
        child: InlineDeckItem,
        sx: {
          maxWidth: 'size-19',
          flexShrink: 1,
          flexBasis: 'size-19'
        },
        imageRef: index === 0 ? elementButtonRelativeTo : null
      }, item), {}, {
        onClick: () => {
          clickEvent(index);
          trackClickEvents.clicked(categories.INLINE_DECK, "".concat(item.title, " | P").concat(index + 1));
        },
        onCreateImpressionWhenView: () => viewEvent(item),
        "data-id": "inline-deck_inline-deck-reel_ReelButton_AnalyticsTrackerWithFold"
      }), "item-".concat(index));
    })
  });
};